import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { Box } from "@mui/material";

const BASE = 60;

/**
 * Same easing as PatrolImageViewer pan so the circle stays visually locked to the map.
 */
const easeInOutSmooth = (t) => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

function RotatingCircle({
  x = 0,
  y = 0,
  nextPosition = null,
  linePath = null,
  /** When > 0 and slideEndpointsGetterRef is set, samples start/end each frame (smooth while zooming). */
  slideRunId = 0,
  slideEndpointsGetterRef = null,
  /** Fires once per slide when movement starts (origin pin → segment departure / line start). */
  onSlideStart = null,
  isActive = false,
  onMoveComplete = null,
  yaw = 0,
  fov = 60,
  moveDuration = 3000,
  size = 60,
}) {
  const [currentPosition, setCurrentPosition] = useState({ x, y });
  const [isMoving, setIsMoving] = useState(false);
  const isMovingRef = useRef(false);
  const rafRef = useRef(null);
  const onMoveCompleteRef = useRef(onMoveComplete);

  useLayoutEffect(() => {
    onMoveCompleteRef.current = onMoveComplete;
  }, [onMoveComplete]);

  const onSlideStartRef = useRef(onSlideStart);
  useLayoutEffect(() => {
    onSlideStartRef.current = onSlideStart;
  }, [onSlideStart]);

  useLayoutEffect(() => {
    if (!isMovingRef.current) {
      setCurrentPosition({ x, y });
    }
  }, [x, y]);

  useEffect(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const useDynamic =
      slideRunId > 0 &&
      slideEndpointsGetterRef != null &&
      typeof slideEndpointsGetterRef.current === "function";

    if (useDynamic) {
      const initial = slideEndpointsGetterRef.current?.();
      if (!initial?.start || !initial?.end) {
        isMovingRef.current = false;
        setIsMoving(false);
        return;
      }

      isMovingRef.current = true;
      setIsMoving(true);
      setCurrentPosition(initial.start);

      if (typeof onSlideStartRef.current === "function") {
        onSlideStartRef.current();
      }

      let startTime = null;

      const tick = (now) => {
        if (startTime == null) startTime = now;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / moveDuration, 1);
        const ease = easeInOutSmooth(progress);
        const frame = slideEndpointsGetterRef.current?.();
        if (!frame?.start || !frame?.end) {
          rafRef.current = null;
          isMovingRef.current = false;
          setIsMoving(false);
          return;
        }
        const { start, end } = frame;
        setCurrentPosition({
          x: start.x + (end.x - start.x) * ease,
          y: start.y + (end.y - start.y) * ease,
        });

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = null;
          isMovingRef.current = false;
          setIsMoving(false);
          setCurrentPosition(end);
          onMoveCompleteRef.current?.();
        }
      };

      rafRef.current = requestAnimationFrame(tick);

      return () => {
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        isMovingRef.current = false;
        setIsMoving(false);
      };
    }

    const startX = linePath?.x1;
    const startY = linePath?.y1;
    const endX = linePath?.x2;
    const endY = linePath?.y2;
    const nextX = nextPosition?.x;
    const nextY = nextPosition?.y;

    if (
      startX == null ||
      startY == null ||
      endX == null ||
      endY == null ||
      nextX == null ||
      nextY == null
    ) {
      isMovingRef.current = false;
      setIsMoving(false);
      return;
    }

    isMovingRef.current = true;
    setIsMoving(true);

    const start = { x: startX, y: startY };
    const end = { x: nextX, y: nextY };
    setCurrentPosition(start);

    let startTime = null;

    const tick = (now) => {
      if (startTime == null) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / moveDuration, 1);
      const ease = easeInOutSmooth(progress);
      setCurrentPosition({
        x: start.x + (end.x - start.x) * ease,
        y: start.y + (end.y - start.y) * ease,
      });

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        isMovingRef.current = false;
        setIsMoving(false);
        setCurrentPosition(end);
        onMoveCompleteRef.current?.();
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      isMovingRef.current = false;
      setIsMoving(false);
    };
  }, [
    slideRunId,
    slideEndpointsGetterRef,
    onSlideStart,
    moveDuration,
    linePath?.x1,
    linePath?.y1,
    linePath?.x2,
    linePath?.y2,
    nextPosition?.x,
    nextPosition?.y,
  ]);

  if (!isActive) return null;

  const center = BASE / 2;
  const borderWidth = Math.max(1.5, size * (4 / BASE));
  const outerRadius = BASE / 1.8 - (BASE * 0.04) / 2;
  const innerRadius = BASE * 0.2;
  const gap = BASE * 0.06;
  const sweepInnerRadius = innerRadius + gap;

  const angleToRad = (a) => (Math.PI / 180) * a;

  const startAngle = -fov / 2 - 90;
  const endAngle = fov / 2 - 90;

  const xOuterStart =
    center + outerRadius * Math.cos(angleToRad(startAngle));
  const yOuterStart =
    center + outerRadius * Math.sin(angleToRad(startAngle));

  const xOuterEnd =
    center + outerRadius * Math.cos(angleToRad(endAngle));
  const yOuterEnd =
    center + outerRadius * Math.sin(angleToRad(endAngle));

  const xInnerStart =
    center + sweepInnerRadius * Math.cos(angleToRad(endAngle));
  const yInnerStart =
    center + sweepInnerRadius * Math.sin(angleToRad(endAngle));

  const xInnerEnd =
    center + sweepInnerRadius * Math.cos(angleToRad(startAngle));
  const yInnerEnd =
    center + sweepInnerRadius * Math.sin(angleToRad(startAngle));

  const largeArcFlag = fov > 180 ? 1 : 0;

  const sweepPath = `
    M ${xOuterStart} ${yOuterStart}
    A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${xOuterEnd} ${yOuterEnd}
    L ${xInnerStart} ${yInnerStart}
    A ${sweepInnerRadius} ${sweepInnerRadius} 0 ${largeArcFlag} 0 ${xInnerEnd} ${yInnerEnd}
    Z
  `;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        position: "absolute",
        left: currentPosition.x,
        top: currentPosition.y,
        transform: "translate(-50%, -50%)",
        transformOrigin: "50% 50%",
        transition: "none",
        borderWidth: `${borderWidth}px`,
        borderStyle: "solid",
        borderColor: "#1BC800",
        borderRadius: "50%",
        boxSizing: "border-box",
        willChange: isMoving ? "left, top" : "auto",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${BASE} ${BASE}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          fill="#b9efb6"
          opacity="0.9"
        />

        <g transform={`rotate(${yaw} ${center} ${center})`}>
          <path d={sweepPath} fill="#1BC800" opacity="0.8" />
        </g>
        <circle
          cx={center}
          cy={center}
          r={innerRadius * 1.33}
          fill="green"
          stroke="white"
          strokeWidth={2}
        />
        <circle cx={center} cy={center} r={innerRadius} fill="#1BC800" />
      </svg>
    </Box>
  );
}

export default RotatingCircle;
